import React from "react";

import { cn } from "@/lib/utils";

/**
 * A short note beside the control it explains.
 *
 * Two tones, and they mean different things:
 *
 * - `warning` — something is off, or a choice has a consequence. Amber, from
 *   the theme's own `tone-amber`, so it reads in light and in dark.
 * - `neutral` — a fact about the deployment the reader has to know, with no
 *   fault in it. The plain surface, because an amber box for "no model is
 *   configured" reads as an error nobody can fix from that screen.
 * - `info` — the machine did something the reader should check before acting
 *   on it. Purple, the colour this app already gives its AI actions, so an AI
 *   draft is not mistaken for a warning about one.
 *
 * Icons are the caller's: the note says which icon fits it. `size` is the text
 * size, and every callout in the app uses one of the three.
 *
 * @param {"warning"|"neutral"|"info"} [tone]
 * @param {Function} [icon]  a lucide icon component
 * @param {"xs"|"sm"|"md"} [size]  11px, 12px or 13px
 */
const TONES = {
  warning: { box: "border-tone-amber/30 bg-tone-amber/5", icon: "text-tone-amber" },
  neutral: { box: "border-border-primary/60 bg-background-primary/40", icon: "text-text-secondary" },
  info: { box: "border-tone-purple/30 bg-tone-purple-soft", icon: "text-tone-purple" },
};

const SIZES = {
  xs: "text-[11px]",
  sm: "text-[12px]",
  md: "text-[13px]",
};

export const Callout = ({ tone = "warning", icon: Icon, size = "md", className = "", iconClassName = "", children, ...props }) => {
  const palette = TONES[tone] ?? TONES.warning;

  return (
    <div className={cn("flex items-start gap-2 rounded-md border p-2.5 leading-relaxed text-text-secondary", palette.box, SIZES[size] ?? SIZES.md, className)} {...props}>
      {Icon ? <Icon className={cn("mt-0.5 h-4 w-4 flex-shrink-0", palette.icon, iconClassName)} aria-hidden="true" /> : null}
      <span className="min-w-0">{children}</span>
    </div>
  );
};

export default Callout;
